// Attribution capture — first-party campaign context for inbound leads.
//
// The audit found the chain broken at its very first link: the site fired GA4,
// Meta and TikTok pixels but captured no utm_* / gclid / fbclid anywhere, so
// `leads.source_id` in the SaaS was always NULL and ad spend could never be tied to
// revenue. This module is that first link.
//
// Model: FIRST-TOUCH and LAST-TOUCH are both kept.
//   first_touch — the campaign that originally found this person. Written once,
//                 then never overwritten for 90 days. This is the one that
//                 deserves credit for the relationship.
//   last_touch  — the campaign active on the visit where they converted.
//                 Overwritten on every visit that carries campaign signal.
// Both travel with the lead; the SaaS reports on last_touch by default and
// keeps the pair in `leads.attribution_json` for when the two disagree.
//
// "Campaign signal" deliberately excludes internal navigation: a click from
// /blog to /contact must not overwrite last_touch with an empty source, which
// is the classic way self-referral destroys attribution data.
//
// Privacy: nothing here is a cross-site identifier and nothing is transmitted
// on page view. The values sit in this browser's localStorage and are sent to
// our own /api/lead endpoint only when the visitor submits the form with the
// explicit consent checkbox ticked. That makes the basis "necessary to handle
// the inquiry", consistent with the rest of the form payload. If you would
// rather gate storage on the analytics cookie choice instead, flip
// REQUIRE_CONSENT to true — capture then degrades to session-only.

const KEY_FIRST = 'fm_attr_first';
const KEY_LAST = 'fm_attr_last';
const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
const MAX_LEN = 200;

// Set to true to store attribution only after the visitor accepts analytics
// cookies. Session-scoped capture still works, so a same-session conversion is
// still attributed; a return visit is not.
const REQUIRE_CONSENT = false;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

// Order = precedence. In practice only one is ever present on a given click.
const CLICK_ID_KEYS = [
  ['gclid', 'gclid'],
  ['fbclid', 'fbclid'],
  ['msclkid', 'msclkid'],
  ['ttclid', 'ttclid'],
];

// Known referrer hosts → (source, medium) when the URL carries no utm_*.
// Keeps organic and social traffic reportable instead of collapsing to
// "direct", which is what makes most attribution dashboards useless.
const REFERRER_MAP = [
  [/(^|\.)google\./, ['google', 'organic']],
  [/(^|\.)bing\./, ['bing', 'organic']],
  [/(^|\.)duckduckgo\./, ['duckduckgo', 'organic']],
  [/(^|\.)ecosia\./, ['ecosia', 'organic']],
  [/(^|\.)facebook\./, ['facebook', 'social']],
  [/(^|\.)instagram\./, ['instagram', 'social']],
  [/(^|\.)linkedin\./, ['linkedin', 'social']],
  [/(^|\.)lnkd\.in/, ['linkedin', 'social']],
  [/(^|\.)t\.co/, ['twitter', 'social']],
  [/(^|\.)x\.com/, ['twitter', 'social']],
  [/(^|\.)tiktok\./, ['tiktok', 'social']],
  [/(^|\.)youtube\./, ['youtube', 'social']],
  [/(^|\.)reddit\./, ['reddit', 'social']],
  [/(^|\.)whatsapp\./, ['whatsapp', 'referral']],
  [/(^|\.)wa\.me/, ['whatsapp', 'referral']],
];

function trunc(v) {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (!t) return null;
  return t.slice(0, MAX_LEN);
}

function analyticsConsented() {
  try {
    const raw = localStorage.getItem('fm_cookies');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.analytics === true || parsed?.marketing === true;
  } catch {
    return false;
  }
}

function canPersist() {
  return !REQUIRE_CONSENT || analyticsConsented();
}

function read(key) {
  try {
    const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function write(key, value) {
  const raw = JSON.stringify(value);
  // sessionStorage always — it is scoped to this tab and carries no
  // cross-session identifier, so it needs no consent gate.
  try { sessionStorage.setItem(key, raw); } catch { /* private mode */ }
  if (!canPersist()) return;
  try { localStorage.setItem(key, raw); } catch { /* quota / private mode */ }
}

const NO_TOUCH = { host: null, source: null, medium: null };

function classifyReferrer(referrer, ownHost) {
  if (!referrer) return NO_TOUCH;
  let host;
  try {
    const u = new URL(referrer);
    // A non-web scheme is not a referrer. Note that `new URL()` happily parses
    // things like `javascript:void(0)` and `mailto:x@y.z` and hands back an
    // EMPTY hostname rather than throwing, so the protocol check has to be
    // explicit — otherwise '' leaks out as the host.
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return NO_TOUCH;
    host = u.hostname.toLowerCase();
    if (!host) return NO_TOUCH;
  } catch {
    return NO_TOUCH;
  }
  // Self-referral is internal navigation, not a new touch.
  if (host === ownHost || host.endsWith('.' + ownHost)) return NO_TOUCH;
  for (const [re, [source, medium]] of REFERRER_MAP) {
    if (re.test(host)) return { host, source, medium };
  }
  return { host, source: host, medium: 'referral' };
}

/**
 * Build a touch record from the current URL + referrer.
 * Returns null when there is no campaign signal at all (direct visit or
 * internal navigation) so callers can leave last_touch untouched.
 */
function readTouch() {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const touch = {};

  for (const k of UTM_KEYS) {
    const v = trunc(params.get(k));
    if (v) touch[k] = v;
  }

  for (const [param, kind] of CLICK_ID_KEYS) {
    const v = trunc(params.get(param));
    if (v) {
      touch.click_id = v;
      touch.click_id_kind = kind;
      break;
    }
  }

  const ref = classifyReferrer(document.referrer, window.location.hostname.toLowerCase());
  if (ref.host) touch.referrer = ref.host;

  const hasCampaign = !!(touch.utm_source || touch.click_id);
  const hasReferrer = !!ref.host;
  if (!hasCampaign && !hasReferrer) return null;

  // No utm_source but we recognized the referrer → infer it, so organic and
  // social do not silently become "direct".
  if (!touch.utm_source && ref.source) {
    touch.utm_source = ref.source;
    if (!touch.utm_medium && ref.medium) touch.utm_medium = ref.medium;
  }
  // A click id with no utm_medium is paid by definition.
  if (touch.click_id && !touch.utm_medium) touch.utm_medium = 'cpc';

  touch.landing_page = trunc(window.location.pathname) || '/';
  touch.ts = new Date().toISOString();
  return touch;
}

/**
 * Call once on app boot, before anything reads attribution.
 * Idempotent: safe under React StrictMode double-invocation.
 */
export function captureAttribution() {
  if (typeof window === 'undefined') return;

  const touch = readTouch();
  if (!touch) return; // direct visit or internal nav — preserve what we have

  write(KEY_LAST, touch);

  const existing = read(KEY_FIRST);
  const stale =
    existing?.ts && Date.now() - new Date(existing.ts).getTime() > FIRST_TOUCH_TTL_MS;
  if (!existing || stale) write(KEY_FIRST, touch);
}

/**
 * Flat attribution payload for the lead form.
 * Shape matches what api/lead.js forwards and what the SaaS lead-intake
 * Edge Function accepts. Returns null when we know nothing — the SaaS then
 * records the lead as `direct` rather than inventing a source.
 */
export function getAttribution() {
  if (typeof window === 'undefined') return null;

  const first = read(KEY_FIRST);
  const last = read(KEY_LAST);
  const primary = last || first;
  if (!primary) return null;

  return {
    utm_source: primary.utm_source ?? null,
    utm_medium: primary.utm_medium ?? null,
    utm_campaign: primary.utm_campaign ?? null,
    utm_term: primary.utm_term ?? null,
    utm_content: primary.utm_content ?? null,
    click_id: primary.click_id ?? null,
    click_id_kind: primary.click_id_kind ?? null,
    landing_page: primary.landing_page ?? null,
    referrer: primary.referrer ?? null,
    // The pair, so the SaaS can show "found by X, converted on Y".
    first_touch: first ?? null,
    last_touch: last ?? null,
  };
}

/** Test seam — clears stored touches. */
export function _resetAttribution() {
  for (const k of [KEY_FIRST, KEY_LAST]) {
    try { sessionStorage.removeItem(k); } catch { /* noop */ }
    try { localStorage.removeItem(k); } catch { /* noop */ }
  }
}

export const _internal = { readTouch, classifyReferrer, REFERRER_MAP, KEY_FIRST, KEY_LAST };
